import React, { useState, useEffect } from "react";
import { Box, Grid, Tab, Tabs } from "@mui/material";
import { StructuredColumnData } from "../../../services/utils/mergeResourceColumns";

interface AvailableColumnsTabsProps {
  availColumns: string[] | StructuredColumnData;
  handleColumnSelect: (column: string) => void;
  isQueryColumns?: boolean;
}

const AvailableColumnsTabs: React.FC<AvailableColumnsTabsProps> = ({
  availColumns,
  handleColumnSelect,
  isQueryColumns,
}) => {
  const [selectedTab, setSelectedTab] = useState<string>("main");

  // Determine if we have structured data or simple array
  const isStructured =
    typeof availColumns === "object" &&
    "type" in availColumns &&
    availColumns.type === "structured";

  // Handle Tab Change
  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  // Reset to first tab when data changes
  useEffect(() => {
    if (isStructured && availColumns.additionalResources?.length) {
      setSelectedTab("main");
    } else {
      setSelectedTab("main");
    }
  }, [availColumns, isStructured]);

  // Render simple array (backward compatibility)
  if (!isStructured || (availColumns as any).type === "simple") {
    const columns = Array.isArray(availColumns)
      ? availColumns
      : (availColumns as StructuredColumnData).flatColumns || [];

    if (columns.length === 0) {
      return null;
    }

    return (
      <Grid item xs={12} md={isQueryColumns ? 12 : 6}>
        <Box sx={{ marginBottom: 3 }} className="available-box box-alt avc">
          <h3 className="mb-0 mb-8">Available Columns abc:</h3>
          <div className="avail-columns-group">
            {columns.map((column, index) => (
              <button
                className="avail-columns"
                key={index}
                onClick={() => handleColumnSelect(column)}
              >
                {column}
              </button>
            ))}
          </div>
        </Box>
      </Grid>
    );
  }

  // Render structured data with tabs
  const structuredData = availColumns as StructuredColumnData;
  const baseColumns = structuredData.baseResource?.columns || [];
  const additionalResources = structuredData.additionalResources || [];

  // Build available tabs
  const availableTabs: { key: string; label: string }[] = [];

  if (baseColumns.length > 0) {
    availableTabs.push({ key: "main", label: "Main Resource Columns" });
  }

  additionalResources.forEach((resource) => {
    if (resource.columns.length > 0) {
      availableTabs.push({
        key: `resource_${resource.resourceId}`,
        label: resource.resourceName,
      });
    }
  });

  if (availableTabs.length === 0) {
    return null;
  }

  return (
    <Grid item xs={12} md={isQueryColumns ? 12 : 6}>
      <Box className="available-box box-alt avc">
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          textColor="secondary"
          indicatorColor="secondary"
          className="rs-query-tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            maxWidth: "100%",
            "& .MuiTabs-scroller": {
              overflow: "auto !important",
            },
            "& .MuiTabs-flexContainer": {
              flexWrap: "nowrap",
            },
          }}
        >
          {availableTabs.map((tab) => (
            <Tab key={tab.key} value={tab.key} label={tab.label} />
          ))}
        </Tabs>

        <Box className="rs-query-tabs-content" sx={{ marginTop: 2 }}>
          {selectedTab === "main" && (
            <div className="avail-columns-group">
              {baseColumns.map((column, index) => (
                <button
                  className="avail-columns"
                  key={index}
                  onClick={() => handleColumnSelect(column)}
                >
                  {column}
                </button>
              ))}
            </div>
          )}

          {additionalResources.map((resource) => {
            const tabKey = `resource_${resource.resourceId}`;
            if (selectedTab === tabKey) {
              return (
                <div key={tabKey} className="avail-columns-group">
                  {resource.columns.map((col, index) => (
                    <button
                      className="avail-columns"
                      key={index}
                      onClick={() =>
                        handleColumnSelect(
                          col.hasConflict ? col.displayName : col.originalName,
                        )
                      }
                      title={
                        col.hasConflict
                          ? `Column has conflict. Will insert as: ${col.displayName}`
                          : col.originalName
                      }
                    >
                      {col.originalName}
                    </button>
                  ))}
                </div>
              );
            }
            return null;
          })}
        </Box>
      </Box>
    </Grid>
  );
};

export default AvailableColumnsTabs;
